'use client';

import * as React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';

type Quote = {
  text: string;
  role: string;
  company: string;
};

// Abu, Oct 29: the v4 pull quote plus the Oct 16 GTM deck's quotes, turning
// slowly. Attributions stay anonymous, as in the deck. The VP of Marketing
// quote is the deck quote with the profanity removed (Abu, Oct 29).
// Nov 2: the champion and personalization quotes moved into the warm
// section's tabs, so the carousel carries two from the Series D AE call
// instead, trimmed of filler only (Abu).
const QUOTES: Quote[] = [
  {
    text: 'I’d rather spend time reaching out to people than trying to find people to reach out to.',
    role: 'Account executive',
    company: 'Series D company'
  },
  {
    text: 'I don’t care about just another automation. I need to see the intelligence is correct.',
    role: 'VP of Marketing',
    company: 'Series A security company, the day before they bought'
  },
  {
    text: 'It’s better quality outreach too, because you can really find what to reach out about and tailor it.',
    role: 'Account executive',
    company: 'Series D company'
  },
  {
    text: 'More responses in the first 24 hours than in two weeks on Monaco.',
    role: 'Founder',
    company: 'after running Monaco and Lumnis side by side'
  }
];

const TURN_MS = 7000;

/** The customer quotes, one at a time; pauses while the pointer is over it. */
export function PipelineQuotes(): React.JSX.Element {
  const shouldReduceMotion = useReducedMotion();
  const [index, setIndex] = React.useState(0);
  const [paused, setPaused] = React.useState(false);

  React.useEffect(() => {
    if (paused) return;
    const timer = window.setTimeout(
      () => setIndex((current) => (current + 1) % QUOTES.length),
      TURN_MS
    );
    return () => window.clearTimeout(timer);
  }, [index, paused]);

  const quote = QUOTES[index]!;

  return (
    <section
      aria-label="What customers say"
      className="relative overflow-hidden bg-[color:var(--paper)] py-20 md:py-28"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div aria-hidden className="hero-halftone-bg" />
      <div className="relative mx-auto w-full max-w-[1320px] px-6 md:px-8 lg:px-12 xl:px-16">
        <div className="mx-auto grid min-h-[300px] max-w-[980px] place-items-center md:min-h-[340px]">
          <AnimatePresence mode="wait">
            <motion.figure
              key={index}
              className="m-0 text-center"
              initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: shouldReduceMotion ? 0 : -12 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
            >
              <blockquote
                className="display-h2 m-0 text-3xl text-[color:var(--ink)] [text-wrap:balance] sm:text-4xl md:text-5xl"
                style={{ letterSpacing: '-0.025em', lineHeight: 1.1 }}
              >
                {`“${quote.text}”`}
              </blockquote>
              <figcaption className="mt-8 flex flex-wrap items-center justify-center gap-2 text-[13px] text-[color:var(--ink-4)]">
                <span className="font-medium text-[color:var(--ink-2)]">
                  {quote.role}
                </span>
                <span>·</span>
                <span>{quote.company}</span>
              </figcaption>
            </motion.figure>
          </AnimatePresence>
        </div>
        <div className="mt-6 flex justify-center gap-2">
          {QUOTES.map((item, i) => (
            <button
              key={item.text}
              type="button"
              aria-label={`Show quote ${i + 1}`}
              aria-current={i === index}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all ${i === index ? 'w-6 bg-[color:var(--ink)]' : 'w-1.5 bg-[color:var(--ink-4)]/40'}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
